/**
 * CompanionDynamic — renders content that Jason has saved via the
 * /admin-companion editor in the LMS portal.
 *
 * Placed after the hardcoded TSX lesson body inside LessonShell.
 * Renders nothing at all when all fields are empty — zero visible change
 * for lessons that haven't been migrated to the sheet yet.
 *
 * Fields rendered:
 *   proseHtml         — rich-text theory notes (HTML from TipTap)
 *   notationImagesJson — handwritten note images (zoomable, same as NoteImage)
 *   midiUrl           — MIDI file download link
 */

import { NoteImage } from "@/components/mt/NoteImage";
import type { CompanionLesson } from "@/hooks/useCompanionData";

interface Props {
  data: CompanionLesson;
  accent?: { hex: string };
}

const DEFAULT_ACCENT = { hex: "#f59e0b" };

/** Colour-code harmonic function in the roman-numeral table. */
function functionColor(fn: string): string {
  const f = (fn || "").toLowerCase();
  if (f.startsWith("tonic")) return "#22c55e";       // home — green
  if (f.startsWith("subdominant")) return "#f59e0b"; // away — amber
  if (f.startsWith("dominant")) return "#f43f5e";    // tension — rose
  return "#94a3b8";
}

export function CompanionDynamic({
  data,
  accent = DEFAULT_ACCENT,
}: Props) {
  // Fields are normalised (never undefined) at the useCompanionData boundary.
  const theoryBoxes = data.theoryBoxesJson.filter(
    (b) => b && ((b.label ?? "").trim() || (b.contentHtml ?? "").trim())
  );
  const rnRows = data.romanNumeralsJson.filter(
    (r) => r && ((r.chord ?? "").trim() || (r.rn ?? "").trim())
  );
  const hasContent =
    !!data.proseHtml.trim() ||
    theoryBoxes.length > 0 ||
    rnRows.length > 0 ||
    data.notationImagesJson.length > 0 ||
    !!data.midiUrl.trim();

  if (!hasContent) return null;

  return (
    <div className="mx-auto max-w-5xl px-4 pb-8 lg:px-8">
      {/* Divider + label */}
      <div className="mt-10 mb-6 flex items-center gap-3">
        <div
          className="h-px flex-1"
          style={{ background: `${accent.hex}40` }}
        />
        <span
          className="text-[10px] font-bold uppercase tracking-[0.22em]"
          style={{ color: `${accent.hex}cc` }}
        >
          ✦ Teaching notes
        </span>
        <div
          className="h-px flex-1"
          style={{ background: `${accent.hex}40` }}
        />
      </div>

      <div className="space-y-6">
        {/* Rich-text theory prose */}
        {data.proseHtml.trim() && (
          <div
            className="prose-nsm space-y-4 text-[15.5px] leading-[1.78] text-nsm-ink/95"
            dangerouslySetInnerHTML={{ __html: data.proseHtml }}
          />
        )}

        {/* Theory boxes — label eyebrow + rich HTML body */}
        {theoryBoxes.length > 0 && (
          <div className="space-y-4">
            {theoryBoxes.map((box, i) => (
              <div
                key={`theory-box-${i}`}
                className="rounded-xl border border-nsm-border bg-nsm-card/40 p-4"
                style={{ borderLeft: `4px solid ${accent.hex}` }}
              >
                {(box.label ?? "").trim() && (
                  <div
                    className="mb-2 text-[10px] font-bold uppercase tracking-[0.22em]"
                    style={{ color: accent.hex }}
                  >
                    {box.label}
                  </div>
                )}
                <div
                  className="prose-nsm space-y-3 text-[15px] leading-[1.75] text-nsm-ink/95"
                  dangerouslySetInnerHTML={{ __html: box.contentHtml ?? "" }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Roman numeral / chord-function analysis table */}
        {rnRows.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-nsm-border bg-nsm-card/40">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-nsm-border text-[10px] uppercase tracking-[0.18em] text-nsm-dim">
                  <th className="px-4 py-2.5 font-bold">Chord</th>
                  <th className="px-4 py-2.5 font-bold">Roman numeral</th>
                  <th className="px-4 py-2.5 font-bold">Function</th>
                </tr>
              </thead>
              <tbody>
                {rnRows.map((row, i) => (
                  <tr key={`rn-row-${i}`} className="border-b border-nsm-border/50 last:border-b-0">
                    <td className="px-4 py-2.5 font-semibold text-nsm-ink">{row.chord}</td>
                    <td className="px-4 py-2.5 font-mono text-[15px]" style={{ color: accent.hex }}>
                      {row.rn}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className="inline-flex items-center gap-1.5 text-[13px] font-semibold"
                        style={{ color: functionColor(row.function) }}
                      >
                        <span
                          className="inline-block h-2 w-2 rounded-full"
                          style={{ background: functionColor(row.function) }}
                        />
                        {row.function || "—"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Handwritten notation images */}
        {data.notationImagesJson.length > 0 && (
          <div className="space-y-4">
            {data.notationImagesJson.map((src, i) => (
              <NoteImage key={`dynamic-img-${i}`} src={src} />
            ))}
          </div>
        )}

        {/* MIDI download */}
        {data.midiUrl.trim() && (
          <div>
            <a
              href={data.midiUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-nsm-border bg-nsm-card/40 px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-nsm-card/70"
              style={{ color: accent.hex }}
            >
              <span>🎹</span>
              <span>Download MIDI file</span>
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
